import React, { useEffect, useState, useContext } from 'react';
import { userAPI } from '../api/endpoints';
import { AuthContext } from '../context/AuthContext';
import './UsersManager.css';

interface User {
  _id: string;
  email: string;
  name: string;
  role: 'admin' | 'manager' | 'viewer';
  isActive: boolean;
  authProvider: 'local' | 'sso';
  lastLogin?: string;
}

const ROLES: User['role'][] = ['admin', 'manager', 'viewer'];
const ROLE_LABEL: Record<User['role'], string> = {
  admin: 'Administrator', manager: 'Manager', viewer: 'Betrachter',
};
const ROLE_HINT: Record<User['role'], string> = {
  admin: 'Voller Zugriff inkl. Benutzerverwaltung',
  manager: 'Inhalte, Seiten, Schedules, Infoscreens verwalten',
  viewer: 'Nur Ansicht',
};

const emptyForm = { email: '', name: '', password: '', role: 'viewer' as User['role'] };

export const UsersManager: React.FC = () => {
  const auth = useContext(AuthContext);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editId, setEditId] = useState<string | null>(null);
  const [editPw, setEditPw] = useState('');

  const fetchUsers = async () => {
    try {
      const res = await userAPI.getAll();
      setUsers(res.data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async () => {
    try {
      await userAPI.create(form);
      setForm({ ...emptyForm });
      setShowCreate(false);
      fetchUsers();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Fehler beim Anlegen');
    }
  };

  const patch = async (id: string, data: any) => {
    try {
      await userAPI.update(id, data);
      fetchUsers();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async (u: User) => {
    if (!window.confirm(`Benutzer "${u.name}" (${u.email}) wirklich löschen?`)) return;
    try {
      await userAPI.delete(u._id);
      fetchUsers();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Fehler beim Löschen');
    }
  };

  const savePassword = async (id: string) => {
    if (!editPw) return;
    await patch(id, { password: editPw });
    setEditId(null);
    setEditPw('');
  };

  const isSelf = (u: User) => auth.user?.id === u._id;

  return (
    <div className="users-page">
      <div className="users-header">
        <div>
          <h1>Benutzerverwaltung</h1>
          <p className="users-sub">Benutzer, Rollen und Zugriff verwalten.</p>
        </div>
        <button className="btn-primary" onClick={() => { setShowCreate(v => !v); setForm({ ...emptyForm }); }}>
          {showCreate ? 'Abbrechen' : '+ Neuer Benutzer'}
        </button>
      </div>

      {showCreate && (
        <div className="user-create-card">
          <h3>Neuen Benutzer anlegen</h3>
          <div className="user-create-grid">
            <label>Name
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Vor- und Nachname" />
            </label>
            <label>E-Mail
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="name@example.com" />
            </label>
            <label>Passwort
              <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Initialpasswort" />
            </label>
            <label>Rolle
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value as User['role'] })}>
                {ROLES.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
              </select>
            </label>
          </div>
          <div className="user-create-actions">
            <button className="btn-primary" onClick={handleCreate} disabled={!form.email || !form.name || !form.password}>Anlegen</button>
          </div>
        </div>
      )}

      {loading ? <p className="users-empty">Laden…</p> : (
        <div className="users-table">
          <div className="users-row users-row--head">
            <span>Benutzer</span><span>Rolle</span><span>Status</span><span>Quelle</span><span>Aktionen</span>
          </div>
          {users.map(u => (
            <div key={u._id} className="users-row">
              <div className="user-cell-main">
                <div className="user-avatar">{(u.name || u.email).charAt(0).toUpperCase()}</div>
                <div>
                  <div className="user-name">{u.name} {isSelf(u) && <span className="user-you">Du</span>}</div>
                  <div className="user-email">{u.email}</div>
                </div>
              </div>
              <div>
                <select
                  className="role-select"
                  value={u.role}
                  disabled={isSelf(u)}
                  onChange={e => patch(u._id, { role: e.target.value })}
                  title={ROLE_HINT[u.role]}
                >
                  {ROLES.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                </select>
              </div>
              <div>
                <button
                  className={`status-toggle ${u.isActive ? 'active' : 'inactive'}`}
                  disabled={isSelf(u)}
                  onClick={() => patch(u._id, { isActive: !u.isActive })}
                >
                  {u.isActive ? 'Aktiv' : 'Deaktiviert'}
                </button>
              </div>
              <div><span className={`provider-badge ${u.authProvider}`}>{u.authProvider === 'sso' ? 'SSO' : 'Lokal'}</span></div>
              <div className="user-actions">
                {u.authProvider === 'local' && (
                  editId === u._id ? (
                    <span className="pw-edit">
                      <input type="password" value={editPw} onChange={e => setEditPw(e.target.value)} placeholder="Neues PW" />
                      <button className="btn-tiny" onClick={() => savePassword(u._id)}>✓</button>
                      <button className="btn-tiny" onClick={() => { setEditId(null); setEditPw(''); }}>✕</button>
                    </span>
                  ) : (
                    <button className="btn-tiny" title="Passwort ändern" onClick={() => { setEditId(u._id); setEditPw(''); }}>🔑</button>
                  )
                )}
                {!isSelf(u) && <button className="btn-tiny btn-danger" title="Löschen" onClick={() => handleDelete(u)}>🗑️</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
